// The row grammar of the INBOX (docs/design/inbox.md §6; round 089 cards
// 03 and 04): the bucket icon in a 24 px gutter, line 1 (the type word in
// a column of 10 characters, the subject, the stamp) and line 2 (the
// figure strip), in one 44 px row. NEEDS YOU (lane N of the build plan)
// draws its items from the same four parts: a host of the class `ib-row`
// lays out `bucketIcon` in a `span.ib-g`, `lineOneHtml` and `lineTwoHtml`
// on its grid, and `pressesHtml` under it.
//
// A cell never moves: a long name ends in an ellipsis, a zero reads as a
// faint dash and an absent mark draws hollow (build plan §1 rule 1). The
// fold count stands in the gutter. A bucket shows by its icon and its
// ink, and no bucket takes the gold (rule 2).

import { INBOX_BUCKETS, type InboxBucket } from '../../gen/balance';
import type { InboxMsg } from '../../gen/proto/aquaphobia';
import { inbox as S } from '../../gen/strings';
import { costText, type GameState } from '../../state';
import { esc } from '../esc';
import { withResIcons } from '../resicon';
import { inboxIcon } from './icons';
import {
  DASH, type Cell, type Facts, cellsOf, factsOf, fig, isClanCopy, markHtml, memberFacts, placeOf, placeText, shortStamp, square,
  subjectOf, typeWord,
} from './words';

// ---------------------------------------------------------------------------
// The bucket
// ---------------------------------------------------------------------------

/** True when `raw` names a bucket. */
export function isBucket(raw: string): raw is InboxBucket {
  return (INBOX_BUCKETS as readonly string[]).includes(raw);
}

/** The bucket of a message. A message filed before the build carries the
 *  bucket of its kind (inbox.md §3); an empty one reads as WORLD, the
 *  plain row of 089 · 13. */
export function bucketOf(m: InboxMsg): InboxBucket {
  return isBucket(m.bucket) ? m.bucket : 'world';
}

/** The tag of a bucket (LOSS, WAR, …). */
export function bucketTag(b: InboxBucket): string {
  return S.bucket_tags[INBOX_BUCKETS.indexOf(b)] ?? '';
}

/** What a screen reader says for a bucket. */
export function bucketName(b: InboxBucket): string {
  return S.bucket_names[INBOX_BUCKETS.indexOf(b)] ?? '';
}

/** The drawn icon of a bucket (`InboxMsg.bucket`), as inline SVG. An
 *  unknown bucket draws the WORLD buoy. */
export function bucketIcon(bucket: string, size = 14): string {
  return inboxIcon(isBucket(bucket) ? bucket : 'world', size);
}

// ---------------------------------------------------------------------------
// The two lines
// ---------------------------------------------------------------------------

/** The stamp column: the time today, the month and the day before; a
 *  fold of today reads its range. */
function stampOf(f: Facts): string {
  if (f.count && f.first > 0 && f.first !== f.unix && shortStamp(f.unix, f.now).includes(':')) {
    return S.range({ first: shortStamp(f.first, f.now), last: shortStamp(f.unix, f.now) });
  }
  return shortStamp(f.unix, f.now);
}

/** Line 1 from facts: the unread mark, the type word, the subject, the
 *  stamp. The unread mark is the accent tick of 089 · 02 at the left edge
 *  of its host (the sheet stands it there), with the word for a screen
 *  reader, so a message carries one unread mark wherever it shows. */
function lineOneOf(f: Facts, read: boolean, word = typeWord(f)): string {
  const subject = subjectOf(f);
  return (read ? '' : `<span class="ib-u"><span class="ib-sr">${esc(S.unread_mark)}</span></span>`)
    + `<span class="ib-w">${esc(word)}</span>`
    + `<span class="ib-s">${subject ? esc(subject) : DASH}</span>`
    + `<span class="ib-st">${esc(stampOf(f))}</span>`;
}

/** The word of the type column: the type word, or the tag of the bucket
 *  for a message that has no type word (filed before the build). */
function kindWord(m: InboxMsg, f: Facts): string {
  return typeWord(f) || bucketTag(bucketOf(m));
}

/** Line 1 of a row for a host of its own, such as a NEEDS YOU item: the
 *  unread mark, the type word, the subject and the stamp in one
 *  `span.ib-l1`, which lays them out on one line. Inline elements only: a
 *  host may be a button. */
export function lineOneHtml(m: InboxMsg, s: GameState): string {
  const f = factsOf(m, s);
  return `<span class="ib-l1">${lineOneOf(f, s.inboxIsRead(m), kindWord(m, f))}</span>`;
}

/** One figure cell. A figure stands in its width with the rest of its
 *  word after it in the small step, so the figures of one type stand at
 *  one x (089 · 03). */
export function cellHtml(c: Cell): string {
  switch (c.t) {
    case 'num': {
      const zero = c.n === 0;
      const at = c.text.indexOf(String(c.n));
      const before = at < 0 ? '' : c.text.slice(0, at);
      const after = at < 0 ? '' : c.text.slice(at + String(c.n).length);
      const value = zero ? '–' : withResIcons(esc(before)) + fig(c.n);
      const mark = c.mark ? `<span class="ib-mk">${markHtml(c.mark, zero)}</span>` : '';
      return `<span class="ib-c ib-cn w${c.w}${zero ? ' ib-c0' : ''}${c.ink && !zero ? ` ib-${c.ink}` : ''}">`
        + `${mark}<b class="ib-v">${value}</b>${after ? `<small>${esc(after)}</small>` : ''}</span>`;
    }
    case 'txt':
      return `<span class="ib-c ib-ct ${c.w ? `w${c.w}` : 'wr'}${c.right ? ' ib-r' : ''}${c.ink && c.text ? ` ib-${c.ink}` : ''}">`
        + `${c.text ? withResIcons(esc(c.text)) : DASH}</span>`;
    case 'fac':
      return `<span class="ib-c ib-cw ${c.w ? `w${c.w}` : 'wr'}">`
        + `${c.faction > 0 ? square(c.faction) : ''}`
        + `<span>${c.name ? esc(c.name) : DASH}</span></span>`;
    case 'flag':
      return `<span class="ib-c ib-cf${c.on && c.ink ? ` ib-${c.ink}` : ''}">${markHtml(c.mark, !c.on)}</span>`;
  }
}

/** Line 2 from facts: the figure strip. */
function lineTwoOf(f: Facts): string {
  return `<span class="ib-f">${cellsOf(f).map(cellHtml).join('')}</span>`;
}

/** Line 2 of a row: the figure strip. */
export function lineTwoHtml(m: InboxMsg, s: GameState): string {
  return lineTwoOf(factsOf(m, s));
}

// ---------------------------------------------------------------------------
// One row of the record
// ---------------------------------------------------------------------------

/** What a screen reader says for a row: the kind, the subject, unread
 *  and the stamp (audit P13). The glyphs are hidden from it. */
function rowLabel(m: InboxMsg, f: Facts, read: boolean): string {
  const word = typeWord(f) || bucketName(bucketOf(m));
  // A screen reader hears the CLAN mark of a clanmate's copy with the kind.
  const kind = isClanCopy(f) ? `${word} ${S.clan_copy}` : word;
  const subject = f.count ? `${subjectOf(f)}, ${S.sr_fold({ n: f.count })}` : subjectOf(f);
  const stamp = stampOf(f);
  return read ? S.sr_row({ kind, subject, stamp }) : S.sr_row_unread({ kind, subject, stamp });
}

/**
 * One row of the record. A single message opens as its page; a fold
 * unfolds its members in place (comp B), and `unfolded` says it stands
 * open. `read` is the reader's view of the row, with the reads the
 * server has not answered yet (audit P5).
 */
export function rowHtml(
  m: InboxMsg, s: GameState, o: { read: boolean; unfolded?: boolean; after?: string },
): string {
  const f = factsOf(m, s);
  const b = bucketOf(m);
  const fold = f.count > 0;
  const cls = `ib-row b-${b} ${o.read ? 'rd' : 'un'}${fold ? ' fold' : ''}${o.unfolded ? ' on' : ''}`;
  const act = fold ? 'ib-r-fold' : 'ib-r-open';
  const gutter = `<span class="ib-g" aria-hidden="true">${bucketIcon(b)}`
    + `${fold ? `<i class="ib-n">×${fig(f.count)}</i>` : ''}</span>`;
  // The list item holds a native button, so a screen reader hears a press
  // and its state; `aria-expanded` belongs to the button (review K1).
  // `after` stands in the same item: the members of an unfolded fold.
  return `<div class="ib-li" role="listitem"><button class="${cls}" data-act="${act}" data-id="${m.id}"`
    + `${fold ? ` aria-expanded="${o.unfolded ? 'true' : 'false'}" title="${esc(S.fold_title)}"` : ''}`
    + ` aria-label="${esc(rowLabel(m, f, o.read))}">`
    + `${gutter}${lineOneOf(f, o.read, kindWord(m, f))}${lineTwoOf(f)}</button>${o.after ?? ''}</div>`;
}

/** The cells of a member line: its figures and its marks, two at most,
 *  so the subject keeps room on a telephone of 390 px. */
function memberCells(f: Facts): string {
  const cells = cellsOf(f);
  let pick: Cell[] = cells.filter((c) => c.t === 'num' || c.t === 'flag').slice(0, 2);
  if (pick.length === 0) pick = cells.filter((c) => c.t === 'txt').slice(0, 1);
  return pick.map(cellHtml).join('');
}

/** The subject of a member line: what names one member of its fold. */
function memberSubject(f: Facts): string {
  switch (f.ev) {
    case 'torpedo_hit':
    case 'torpedo_struck':
      return f.p.launcher || f.s.torpedo(Number(f.p.torpedo))?.name || '';
    case 'hold_under_fire':
    case 'territory_lost':
      return placeText(f.p, f.s);
    case 'market_sold_isotopes':
    case 'market_sold_print':
      return f.p.buyer || '';
  }
  return subjectOf(f);
}

/**
 * The member lines of a fold (comp B): one 22 px line each, newest
 * first, with its stamp, its subject, its key figures, and SHOW and
 * REPLAY at the right where the member has a cell and a replay. The
 * unfolded row and the page of the fold both draw them.
 */
export function memberLines(m: InboxMsg, s: GameState): string {
  return m.members.map((mem, i) => {
    const f = memberFacts(m, mem, s);
    const subject = memberSubject(f);
    return `<div class="ib-mem" role="listitem">`
      + `<span class="ib-mt">${esc(shortStamp(f.unix, f.now))}</span>`
      + `<span class="ib-ms">${subject ? esc(subject) : DASH}</span>`
      + `<span class="ib-mf">${memberCells(f)}</span>`
      + `<span class="ib-ma">`
      + (mem.cell ? `<button class="ib-mp" data-act="ib-r-show" data-n="${mem.cell}" title="${esc(S.show_title)}">${esc(S.show)}</button>` : '')
      + (mem.replay ? `<button class="ib-mp" data-act="ib-r-mreplay" data-id="${m.id}" data-n="${i}" title="${esc(S.replay_title)}">${esc(S.replay)}</button>` : '')
      + `</span></div>`;
  }).reverse().join('');
}

/** The members of an unfolded fold, in place under its row. The foot
 *  holds READ, which reads the fold whole, and OPEN › to its page. */
export function membersHtml(m: InboxMsg, s: GameState, read: boolean): string {
  return `<div class="ib-fold b-${bucketOf(m)}" role="list">${memberLines(m, s)}`
    + `<div class="ib-ffoot">`
    + (read ? '' : `<button class="ib-p" data-act="ib-r-read" data-id="${m.id}">${esc(S.read)}</button>`)
    + `<button class="ib-p" data-act="ib-r-open" data-id="${m.id}">${esc(S.open_page)}</button>`
    + `</div></div>`;
}

// ---------------------------------------------------------------------------
// The presses
// ---------------------------------------------------------------------------

/** The REPLACE presses sent and not answered yet, with the list they were
 *  pressed on: a second press of the same offer sends nothing until the
 *  next Inbox frame (audit P15). */
const replaceSent = new Map<number, readonly InboxMsg[]>();

/** A REPLACE press went out for message `id`. */
export function noteReplaceSent(id: number, s: GameState): void {
  replaceSent.set(id, s.inbox);
}

/** Is a REPLACE of message `id` on its way? A new list answers it. */
export function replacePending(id: number, s: GameState): boolean {
  const at = replaceSent.get(id);
  if (at === undefined) return false;
  if (at !== s.inbox) {
    replaceSent.delete(id);
    return false;
  }
  return true;
}

/**
 * The Replace action of a loss (fleet-atom-redesign.md §11, R1308): the
 * press with its price, and the note under the presses. The server
 * states the price and the refusal when it sends the list, so this
 * decides nothing. A refusal greys the press out and the note names the
 * reason; `afford` picks the grey, because short of resources is another
 * "no" than a gate (R45). A spent press reads "queued" and offers none.
 */
function replacePress(m: InboxMsg, s: GameState, first: boolean): { press: string; note: string } {
  const r = m.replace;
  if (!r) return { press: '', note: '' };
  if (r.queued) return { press: '', note: `<div class="ib-note">${esc(S.replace_queued)}</div>` };
  const lead = first ? ' dk1' : '';
  if (replacePending(m.id, s)) {
    return { press: `<button class="ib-p${lead}" data-act="ib-r-replace" data-id="${m.id}" disabled>${esc(S.replace_sent)}</button>`, note: '' };
  }
  const refused = r.refusal !== '';
  const afford = s.canAfford(r.price);
  const label = S.replace({ price: `<span class="ib-cost">${costText(r.price)}</span>` });
  return {
    press: `<button class="ib-p ib-rep${lead}${refused && !afford ? ' noafford' : ''}" data-act="ib-r-replace"`
      + ` data-id="${m.id}" title="${esc(refused ? r.refusal : S.replace_title)}"${refused ? ' disabled' : ''}>`
      + `${label}</button>`,
    note: refused ? `<div class="ib-note">${esc(r.refusal)}</div>` : '',
  };
}

/** The live ransom of a captain the loss names, or 0 when the captain is
 *  free again (paid, or freed by the season roll). */
function openRansom(m: InboxMsg, s: GameState): number {
  if (m.params.fate !== 'captured') return 0;
  const id = Number(m.params.captain_id ?? 0);
  if (!(id > 0)) return 0;
  const c = s.captain(id);
  if (c) return c.capturedBy !== '' ? c.ransom || Number(m.params.ransom ?? 0) : 0;
  // A captain not on the roster: the server's NEEDS YOU flag knows.
  return m.needs ? Number(m.params.ransom ?? 0) : 0;
}

/** Has the window of a SIEGE message not ended? */
function windowToCome(m: InboxMsg, now: number): boolean {
  const end = Number(m.params.window_end ?? 0);
  return end > now;
}

/** One press in the dark face (087 · 03). The first press of a row leads
 *  by its width. */
function press(first: boolean, act: string, label: string, attrs: string, title = '', cls = ''): string {
  return `<button class="ib-p${first ? ' dk1' : ''}${cls}" data-act="${act}"${attrs}`
    + `${title ? ` title="${esc(title)}"` : ''}>${label}</button>`;
}

/** GO TO: the map moves to the place of the message. */
export function goToPress(place: { name: string; cell: number }, first = false): string {
  return press(first, 'ib-r-goto', esc(S.go_to), ` data-n="${place.cell}"`, S.go_to_title({ place: place.name }));
}

/**
 * The presses of a message in the dark face: the action first (REPLACE
 * with its price, PAY RANSOM, PLEDGE A FLEET), then REPLAY and GO TO, then
 * the page it leads to (CAPTAINS ›, CLAN PAGE ›, ACHIEVEMENTS ›, REPORT ›). Each
 * carries an `ib-r-` act. The row wraps, so no press leaves the screen
 * (audit P4). The note of a refused REPLACE stands under the row. The
 * open page sets `goTo` false, because its place line holds GO TO.
 */
export function pressesHtml(m: InboxMsg, s: GameState, o: { goTo?: boolean } = {}): string {
  const f = factsOf(m, s);
  const out: string[] = [];
  const first = () => out.length === 0;
  const rep = replacePress(m, s, true);
  if (rep.press) out.push(rep.press);
  const ransom = openRansom(m, s);
  if (ransom > 0) {
    const id = Number(m.params.captain_id);
    const afford = s.res.credits >= ransom;
    // Short of credits is the look of a price the empire cannot pay (R45).
    out.push(press(first(), 'ib-r-ransom', withResIcons(esc(S.pay_ransom({ ransom: fig(ransom) }))),
      ` data-id="${id}"${afford ? '' : ' disabled'}`, '', afford ? '' : ' noafford'));
  }
  const place = placeOf(f);
  if (m.event === 'siege_defend' && windowToCome(m, f.now) && place && place.cell > 0) {
    out.push(press(first(), 'ib-r-pledge', esc(S.pledge), ` data-n="${place.cell}"`));
  }
  if (m.replay) out.push(press(first(), 'ib-r-replay', esc(S.replay), ` data-id="${m.id}"`, S.replay_title));
  if (o.goTo !== false && place && place.cell > 0) out.push(goToPress(place, first()));
  if (m.event === 'captain_taken') out.push(press(first(), 'ib-r-captains', esc(S.captains), ''));
  if (m.event === 'pickup_squad') out.push(press(first(), 'ib-r-hub', esc(S.clan_page), ' data-k="clan"'));
  if (m.event === 'achievement') out.push(press(first(), 'ib-r-hub', esc(S.achievements), ' data-k="record"'));
  // REPORT › of the season report: the season table of the reader on the
  // SEASONS board of RANKS (R3136).
  if (m.event === 'season_closed') out.push(press(first(), 'ib-r-hub', esc(S.report), ' data-k="seasons"', S.report_title));
  if (out.length === 0) return rep.note;
  return `<div class="ib-acts">${out.join('')}</div>${rep.note}`;
}
